// FUNCTIONALITY-115C-R1 — Contact Manager modal: Phone / Viber / Telegram / Close only.
import { useEffect, useId, useRef, useState, type RefObject } from "react";
import type { StoreV2Lang } from "./store-v2-locale.js";
import { openExternalAction, type ExternalActionType } from "./external-link-v2.js";
import { StoreV2Icon, type StoreV2IconName } from "./StoreV2Icon.js";
import { t } from "./store-v2-ui-copy.js";
import { emitEvent } from "./events-v2.js";
import { STORE_INFO } from "./store-info-v2.js";

export type ContactFeedbackKind = "ok" | "err" | "info";

export interface ContactFeedback {
  kind: ContactFeedbackKind;
  text: string;
}

export interface ContactSourceContext {
  source_surface: string;
  source_route?: string;
  source_title?: string;
  locale?: StoreV2Lang;
  product_id?: string | null;
  sku?: string | null;
  product_name?: string | null;
  category?: string | null;
  brand?: string | null;
  order_id?: string | null;
  payment_method?: string | null;
  referral_code?: string | null;
  manager_header?: string;
  [key: string]: unknown;
}

function feedbackClass(kind: ContactFeedbackKind): string {
  if (kind === "err") return "sv2-contact-feedback sv2-contact-feedback-err";
  if (kind === "info") return "sv2-contact-feedback sv2-contact-feedback-info";
  return "sv2-contact-feedback sv2-contact-feedback-ok";
}

/** Shared single-slot contact feedback (never error-red for copy success). */
export function ContactActionFeedback({ feedback }: { feedback: ContactFeedback | null }) {
  if (!feedback) return null;
  return (
    <div className={feedbackClass(feedback.kind)} role="status" aria-live="polite">
      {feedback.text}
    </div>
  );
}

function phoneFallbackMessage(lang: StoreV2Lang): string {
  return lang === "en"
    ? "Calling is not supported on this device. Phone number copied ✓"
    : "Дзвінки не підтримуються на цьому пристрої. Номер скопійовано ✓";
}

function viberFallbackMessage(lang: StoreV2Lang): string {
  return lang === "en"
    ? "Viber could not be opened. Phone number copied ✓"
    : "Не вдалося відкрити Viber. Номер скопійовано ✓";
}

function telegramFallbackMessage(lang: StoreV2Lang): string {
  return lang === "en"
    ? "Telegram could not be opened. Link copied ✓"
    : "Не вдалося відкрити Telegram. Посилання скопійовано ✓";
}

function openingMessage(lang: StoreV2Lang, channel: "phone" | "viber" | "telegram"): string {
  if (channel === "phone") {
    return lang === "en" ? "Opening the phone app…" : "Відкриваємо програму для дзвінка…";
  }
  if (channel === "viber") {
    return lang === "en" ? "Opening Viber…" : "Відкриваємо Viber…";
  }
  return lang === "en" ? "Opening Telegram…" : "Відкриваємо Telegram…";
}

export function ContactActionButton({
  lang,
  type,
  value,
  copyFallback,
  label,
  icon,
  className = "sv2-cta sv2-secondary",
  onFeedback,
  analytics,
}: {
  lang: StoreV2Lang;
  type: ExternalActionType;
  value: string;
  copyFallback?: string;
  label: string;
  icon?: StoreV2IconName;
  className?: string;
  onFeedback?: (feedback: ContactFeedback | null) => void;
  analytics?: ContactSourceContext;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      className={className}
      disabled={busy}
      aria-label={label}
      onClick={() => {
        if (busy) return;
        setBusy(true);
        const channel =
          type === "phone" || type === "viber" || type === "telegram" ? type : "other";
        if (channel !== "other") {
          onFeedback?.({ kind: "info", text: openingMessage(lang, channel) });
        }
        void openExternalAction({
          type,
          value,
          ...(copyFallback ? { copyFallback } : {}),
          properties: {
            ...(analytics ?? {}),
            selected_contact_channel: type,
            locale: lang,
          },
        })
          .then((result) => {
            if (result.kind === "opened") {
              onFeedback?.({
                kind: "info",
                text:
                  channel === "other"
                    ? lang === "en"
                      ? "Opened"
                      : "Відкрито"
                    : openingMessage(lang, channel as "phone" | "viber" | "telegram"),
              });
              return;
            }
            if (type === "phone") {
              onFeedback?.({
                kind: result.kind === "copied" ? "ok" : "err",
                text:
                  result.kind === "copied"
                    ? phoneFallbackMessage(lang)
                    : lang === "en"
                      ? "Could not call or copy the number."
                      : "Не вдалося подзвонити або скопіювати номер.",
              });
              return;
            }
            if (type === "viber") {
              onFeedback?.({
                kind: result.kind === "copied" ? "ok" : "err",
                text:
                  result.kind === "copied"
                    ? viberFallbackMessage(lang)
                    : lang === "en"
                      ? "Could not open Viber or copy the number."
                      : "Не вдалося відкрити Viber або скопіювати номер.",
              });
              return;
            }
            if (type === "telegram") {
              onFeedback?.({
                kind: result.kind === "copied" ? "ok" : "err",
                text:
                  result.kind === "copied"
                    ? telegramFallbackMessage(lang)
                    : lang === "en"
                      ? "Could not open Telegram or copy the link."
                      : "Не вдалося відкрити Telegram або скопіювати посилання.",
              });
              return;
            }
            onFeedback?.({
              kind: result.kind === "copied" ? "ok" : "err",
              text: t(lang, "copied"),
            });
          })
          .catch(() => {
            onFeedback?.({
              kind: "err",
              text:
                lang === "en"
                  ? "Try again or copy the contact manually."
                  : "Спробуйте ще раз або скопіюйте контакт вручну.",
            });
          })
          .finally(() => {
            window.setTimeout(() => setBusy(false), 400);
          });
      }}
    >
      {icon ? <StoreV2Icon name={icon} size={22} /> : null}
      <span>{label}</span>
    </button>
  );
}

/** Legacy multi-action panel (About etc.). Manager modal uses ContactManagerModal. */
export function ContactActionsPanel({
  lang,
  phoneTel,
  phoneDisplay,
  email,
  mapsUrl,
  instagramUrl,
  telegramUrl,
  compact = false,
}: {
  lang: StoreV2Lang;
  phoneTel: string;
  phoneDisplay: string;
  email: string;
  mapsUrl: string;
  instagramUrl: string;
  telegramUrl?: string | null;
  compact?: boolean;
}) {
  const [feedback, setFeedback] = useState<ContactFeedback | null>(null);
  useEffect(() => {
    if (!feedback) return;
    const id = window.setTimeout(() => setFeedback(null), 3200);
    return () => window.clearTimeout(id);
  }, [feedback]);

  if (compact) {
    return (
      <ContactManagerPanel
        lang={lang}
        phoneTel={phoneTel}
        phoneDisplay={phoneDisplay}
        telegramUrl={telegramUrl ?? null}
        analytics={{ source_surface: "profile", locale: lang }}
      />
    );
  }

  return (
    <div className="sv2-contact-actions">
      <ContactActionButton
        lang={lang}
        type="phone"
        value={phoneTel}
        copyFallback={phoneDisplay}
        label={t(lang, "call")}
        icon="phone"
        className="sv2-cta"
        onFeedback={setFeedback}
      />
      <ContactActionButton
        lang={lang}
        type="viber"
        value={phoneTel}
        copyFallback={phoneDisplay}
        label="Viber"
        icon="viber"
        className="sv2-cta"
        onFeedback={setFeedback}
      />
      {telegramUrl ? (
        <ContactActionButton
          lang={lang}
          type="telegram"
          value={telegramUrl}
          label="Telegram"
          icon="telegram"
          className="sv2-cta"
          onFeedback={setFeedback}
        />
      ) : null}
      <ContactActionButton lang={lang} type="email" value={email} label="Email" icon="mail" onFeedback={setFeedback} />
      <ContactActionButton
        lang={lang}
        type="maps"
        value={mapsUrl}
        copyFallback={mapsUrl}
        label="Maps"
        icon="map"
        onFeedback={setFeedback}
      />
      <ContactActionButton
        lang={lang}
        type="instagram"
        value={instagramUrl}
        label="Instagram"
        icon="instagram"
        onFeedback={setFeedback}
      />
      <ContactActionFeedback feedback={feedback} />
    </div>
  );
}

function ContactManagerPanel({
  lang,
  phoneTel,
  phoneDisplay,
  telegramUrl,
  analytics,
  onClose,
  closeLabel,
  onExplicitContact,
}: {
  lang: StoreV2Lang;
  phoneTel: string;
  phoneDisplay: string;
  telegramUrl?: string | null;
  analytics?: ContactSourceContext;
  onClose?: () => void;
  closeLabel?: string;
  /** NOVAPAY-116-R2 — fire only after Phone/Viber/Telegram (not on modal open). */
  onExplicitContact?: (channel: "phone" | "viber" | "telegram") => void;
}) {
  const [feedback, setFeedback] = useState<ContactFeedback | null>(null);
  const lastClickRef = useRef<{ channel: string; at: number }>({ channel: "", at: 0 });

  useEffect(() => {
    if (!feedback) return;
    const id = window.setTimeout(() => setFeedback(null), 3200);
    return () => window.clearTimeout(id);
  }, [feedback]);

  const setFeedbackOnce = (next: ContactFeedback | null) => {
    setFeedback(next);
  };

  const guardRapid = (channel: string): boolean => {
    const now = Date.now();
    if (lastClickRef.current.channel === channel && now - lastClickRef.current.at < 1200) {
      return false;
    }
    lastClickRef.current = { channel, at: now };
    return true;
  };

  const runChannel = (type: "phone" | "viber" | "telegram", value: string, copyFallback?: string) => {
    if (!guardRapid(type)) return;
    setFeedbackOnce({ kind: "info", text: openingMessage(lang, type) });
    onExplicitContact?.(type);
    void openExternalAction({
      type,
      value,
      ...(copyFallback ? { copyFallback } : {}),
      properties: {
        ...(analytics ?? {}),
        selected_contact_channel: type,
        locale: lang,
        fallback_used: false,
      },
      // Modal owns rich analytics; adapter still emits channel + fallback events safely.
    })
      .then((result) => {
        if (result.kind === "opened") {
          setFeedbackOnce({ kind: "info", text: openingMessage(lang, type) });
          return;
        }
        const copiedOk = result.kind === "copied";
        if (type === "phone") {
          setFeedbackOnce({
            kind: copiedOk ? "ok" : "err",
            text: copiedOk
              ? phoneFallbackMessage(lang)
              : lang === "en"
                ? "Could not call or copy the number."
                : "Не вдалося подзвонити або скопіювати номер.",
          });
          return;
        }
        if (type === "viber") {
          setFeedbackOnce({
            kind: copiedOk ? "ok" : "err",
            text: copiedOk
              ? viberFallbackMessage(lang)
              : lang === "en"
                ? "Could not open Viber or copy the number."
                : "Не вдалося відкрити Viber або скопіювати номер.",
          });
          return;
        }
        setFeedbackOnce({
          kind: copiedOk ? "ok" : "err",
          text: copiedOk
            ? telegramFallbackMessage(lang)
            : lang === "en"
              ? "Could not open Telegram or copy the link."
              : "Не вдалося відкрити Telegram або скопіювати посилання.",
        });
      })
      .catch(() => {
        setFeedbackOnce({
          kind: "err",
          text:
            lang === "en"
              ? "Try again or copy the contact manually."
              : "Спробуйте ще раз або скопіюйте контакт вручну.",
        });
      });
  };

  const tgUrl = (telegramUrl ?? "").trim();

  return (
    <div className="sv2-contact-manager" data-testid="sv2-contact-manager">
      <p className="sv2-contact-helper">
        {lang === "en"
          ? "On desktop, if calling or Viber is unavailable, the phone number will be copied."
          : "На комп’ютері, якщо дзвінок або Viber не підтримуються, номер буде скопійовано."}
      </p>
      <div className="sv2-contact-actions sv2-contact-actions-manager">
        <button
          type="button"
          className="sv2-cta sv2-contact-channel"
          aria-label={lang === "en" ? "Call" : "Подзвонити"}
          onClick={() => runChannel("phone", phoneTel, phoneDisplay)}
        >
          <StoreV2Icon name="phone" size={22} />
          <span>{lang === "en" ? "Call" : "Подзвонити"}</span>
        </button>
        <button
          type="button"
          className="sv2-cta sv2-contact-channel"
          aria-label="Viber"
          onClick={() => runChannel("viber", phoneTel, phoneDisplay)}
        >
          <StoreV2Icon name="viber" size={22} />
          <span>Viber</span>
        </button>
        <button
          type="button"
          className="sv2-cta sv2-contact-channel"
          aria-label="Telegram"
          disabled={!tgUrl}
          onClick={() => {
            if (!tgUrl) {
              setFeedbackOnce({
                kind: "err",
                text:
                  lang === "en"
                    ? "Telegram link is not configured."
                    : "Посилання Telegram не налаштовано.",
              });
              return;
            }
            runChannel("telegram", tgUrl, tgUrl);
          }}
        >
          <StoreV2Icon name="telegram" size={22} />
          <span>Telegram</span>
        </button>
      </div>
      <ContactActionFeedback feedback={feedback} />
      {onClose ? (
        <button type="button" className="sv2-cta sv2-secondary" onClick={onClose}>
          {closeLabel ?? (lang === "en" ? "Close" : "Закрити")}
        </button>
      ) : null}
      {/* Explicitly exclude Email / Maps / Instagram / Copy / callback-order from manager modal */}
      <span className="sv2-visually-hidden" data-manager-channels="phone,viber,telegram,close">
        {STORE_INFO.phoneDisplay}
      </span>
    </div>
  );
}

export function ContactManagerModal({
  lang,
  open,
  phoneTel,
  phoneDisplay,
  telegramUrl,
  analytics,
  onClose,
  returnFocusRef,
  onExplicitContact,
}: {
  lang: StoreV2Lang;
  open: boolean;
  phoneTel: string;
  phoneDisplay: string;
  telegramUrl?: string | null;
  analytics?: ContactSourceContext;
  onClose: () => void;
  returnFocusRef?: RefObject<HTMLButtonElement | null>;
  onExplicitContact?: (channel: "phone" | "viber" | "telegram") => void;
}) {
  const titleId = useId();
  const wasOpenRef = useRef(false);
  const analyticsRef = useRef(analytics);
  analyticsRef.current = analytics;

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      wasOpenRef.current = true;
      emitEvent("contact_modal_opened", {
        ...(analyticsRef.current ?? {}),
        locale: lang,
      });
      return;
    }
    if (!open && wasOpenRef.current) {
      wasOpenRef.current = false;
      emitEvent("contact_modal_closed", {
        ...(analyticsRef.current ?? {}),
        locale: lang,
      });
      window.setTimeout(() => returnFocusRef?.current?.focus?.(), 0);
    }
  }, [open, lang, returnFocusRef]);

  if (!open) return null;

  return (
    <div
      className="sv2-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-testid="sv2-contact-manager-modal"
    >
      <div className="sv2-modal-card">
        <div className="sv2-section-title" id={titleId}>
          {lang === "en" ? "Contact manager" : "Зв’язатися з менеджером"}
        </div>
        <ContactManagerPanel
          lang={lang}
          phoneTel={phoneTel}
          phoneDisplay={phoneDisplay}
          telegramUrl={telegramUrl ?? null}
          {...(analytics ? { analytics } : {})}
          onClose={onClose}
          closeLabel={lang === "en" ? "Close" : "Закрити"}
          {...(onExplicitContact ? { onExplicitContact } : {})}
        />
      </div>
    </div>
  );
}
